/**
 * Mobile bottom-sheet drag interactions for the parts pane.
 * Three snap states: peek (just search bar visible), half, full.
 *
 * Only attached on viewports < 900px. Uses pointer events so it
 * works for touch and trackpad alike.
 */
export function attachBottomSheet(pane: HTMLElement) {
  if (window.matchMedia("(min-width: 900px)").matches) {
    pane.classList.remove("peek-half", "peek-full");
    return;
  }

  let startY = 0;
  let baseTranslate = 0;
  let dragging = false;
  let activePointerId: number | null = null;

  const heights = () => ({
    peek: pane.getBoundingClientRect().height - 92,
    half: pane.getBoundingClientRect().height * 0.5,
    full: 0,
  });

  const setTranslate = (px: number) => {
    pane.style.transform = `translateY(${Math.max(0, px)}px)`;
  };

  const applySnap = (key: "peek" | "half" | "full") => {
    pane.classList.remove("peek-half", "peek-full");
    if (key === "half") pane.classList.add("peek-half");
    if (key === "full") pane.classList.add("peek-full");
    pane.style.transform = "";
  };

  const onPointerDown = (e: PointerEvent) => {
    // Only initiate drag if the touch starts on the grab area or top 60px.
    const r = pane.getBoundingClientRect();
    if (e.clientY - r.top > 60) return;
    pane.setPointerCapture(e.pointerId);
    activePointerId = e.pointerId;
    dragging = true;
    pane.classList.add("dragging");
    startY = e.clientY;
    const m = pane.style.transform.match(/translateY\(([\d.-]+)px\)/);
    baseTranslate = m ? Number(m[1]) : currentTranslate(pane);
    e.preventDefault();
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragging || activePointerId !== e.pointerId) return;
    const dy = e.clientY - startY;
    setTranslate(baseTranslate + dy);
  };

  const onPointerUp = (e: PointerEvent) => {
    if (!dragging || activePointerId !== e.pointerId) return;
    dragging = false;
    pane.classList.remove("dragging");
    pane.releasePointerCapture(e.pointerId);
    activePointerId = null;

    const cur = currentTranslate(pane);
    const h = heights();
    const distances = {
      peek: Math.abs(cur - h.peek),
      half: Math.abs(cur - h.half),
      full: Math.abs(cur - h.full),
    };
    const snap = (Object.entries(distances).sort((a, b) => a[1] - b[1])[0][0]) as
      | "peek"
      | "half"
      | "full";
    applySnap(snap);
  };

  pane.addEventListener("pointerdown", onPointerDown);
  pane.addEventListener("pointermove", onPointerMove);
  pane.addEventListener("pointerup", onPointerUp);
  pane.addEventListener("pointercancel", onPointerUp);

  // Resize re-evaluation
  window.addEventListener("resize", () => {
    if (window.matchMedia("(min-width: 900px)").matches) {
      pane.style.transform = "";
      pane.classList.remove("peek-half", "peek-full", "dragging");
    }
  });
}

function currentTranslate(el: HTMLElement): number {
  const m = window.getComputedStyle(el).transform;
  if (!m || m === "none") return 0;
  const match = m.match(/matrix\(([^)]+)\)/);
  if (!match) return 0;
  const vals = match[1].split(",").map(Number);
  return vals[5] ?? 0;
}
